import React, { useEffect, useState, useCallback } from 'react';
import { useBeneficiariesMutation } from 'shared/apollo/pageHooks/useAllReportBeneficiaries';
import { useNewNpoAnswer } from 'shared/apollo/pageHooks/useNewNpoAnswer';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import styled from 'styled-components';
import { BottomBar } from '../BottomBar';
import { BeneficiaryRouteType, BeneficiaryRoutes } from './BeneficiaryRoutes';
import {
  Beneficiaries,
  BeneficiaryQuery,
  BeneficiaryTitleInfoProps,
} from './utils/beneficiaryInterfaces';
import {
  beneficiaryValidations,
  BENEFICIARY_CATEGORIES_KEYS,
} from './utils/helperFunctions';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { transformStringToBeneficiaries } from './utils/transformStringToBeneficiaries';
import { GuidanceTextResponse } from '../utils';

export const validateBeneficiaryNext = (
  route: BeneficiaryRouteType,
  beneficiaries: Beneficiaries,
): boolean => {
  if (route === 'landing') {
    const index = BENEFICIARY_CATEGORIES_KEYS.findIndex(
      x => beneficiaries[x] !== undefined,
    );
    return index !== -1;
  }
  return beneficiaryValidations(beneficiaries);
};

export interface Question {
  id: number; // same as dbId
  dbId: number; // same as id
  autoSave: boolean;
  enumPrograms: [];
  enumResponses: {
    dbId: number;
    nextQuestionId: number | null;
    order: number;
    questionId: number;
    response: string;
    responseHelp: null;
    responseType: 'radio' | string;
    responseValue: null;
    treeGroup: 'Calendar Year' | string;
  }[];
  feExtraRequirement: null;
  helpContent: null;
  nextQuestionId: null;
  questionType: 'RADIO';
  section: FeedbackSectionId;
  sectionStart: boolean;
  tab: null;
  text: string;
  text_html: string;
  validations: [];
}

export interface BeneficiaryLandingProps
  extends Partial<BeneficiaryTitleInfoProps> {
  reportId: number;
  value?: string;
  guidanceText: GuidanceTextResponse | undefined;
  allAnswers: { [id: number]: string | undefined };
  question: Question | undefined;
  beneficiaries: Beneficiaries;
  beneficiariesLoading: boolean;
  beneficiary: string;
  beneficiariesError: BeneficiaryQuery['error'];
  reloadAnswers: () => unknown;
  beneficiariesRefetch: () => unknown;
  onNextSection: () => unknown;
  onPreviousSection: () => unknown;
}

const StyledDiv = styled.div`
  margin-top: ${SPACERS.extraLarge};
  .heading {
    margin-bottom: 24px;
  }
`;

const BeneficiaryLanding: React.FC<BeneficiaryLandingProps> = ({
  value,
  reportId,
  question,
  guidanceText,
  allAnswers,
  beneficiaries: data,
  beneficiariesLoading,
  beneficiary,
  beneficiariesError,
  reloadAnswers,
  beneficiariesRefetch,
  programName = 'your program',
  onNextSection,
  onPreviousSection,
}) => {
  const [beneficiaries, setBeneficiaries] = useState<Beneficiaries>(
    transformStringToBeneficiaries(data, value),
  );
  const { run } = useBeneficiariesMutation();
  const { run: runUpdateAnswer } = useNewNpoAnswer();
  const [route, setRoute] = useState<BeneficiaryRouteType>(
    validateBeneficiaryNext('landing', data) ? 'categories' : 'landing',
  );

  const handleNext = useCallback(() => {
    if (route === 'landing') return setRoute('categories');
    onNextSection();
    runUpdateAnswer(
      {
        reportId,
        questionId: 60001,
        text: JSON.stringify(beneficiaries),
      },
      () => reloadAnswers(),
    );
  }, [beneficiaries, route, reportId, setRoute, onNextSection]);

  const handlePrevious = useCallback(() => {
    if (route === 'categories') return setRoute('landing');
    onPreviousSection();
  }, [route, setRoute, onPreviousSection]);

  const handleRefetch = useCallback(() => {
    beneficiariesRefetch();
  }, [beneficiariesRefetch]);

  useEffect(() => {
    beneficiariesRefetch();
  }, []);

  useEffect(() => {
    setBeneficiaries(transformStringToBeneficiaries(data, value));
  }, [setBeneficiaries, data]);

  useEffect(() => {
    if (route === 'landing') return;
    beneficiaryValidations(beneficiaries) && run(reportId, beneficiaries);
  }, [route, reportId, beneficiaries]);

  return (
    <StyledDiv>
      <BeneficiaryRoutes
        route={route}
        guidanceText={guidanceText}
        allAnswers={allAnswers}
        error={beneficiariesError}
        loading={beneficiariesLoading}
        refetch={handleRefetch}
        beneficiaries={beneficiaries}
        setBeneficiaries={setBeneficiaries}
        goBack={handlePrevious}
        programName={programName}
        beneficiary={beneficiary}
      />
      <BottomBar
        disabled={false}
        hideNext={!validateBeneficiaryNext(route, beneficiaries)}
        backTitle="Back"
        nextTitle="Save &amp; Continue"
        handleNext={handleNext}
        handleBack={handlePrevious}
      />
    </StyledDiv>
  );
};

export default React.memo(BeneficiaryLanding);
